import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnInit,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { InspectionDetailsService } from "src/app/inspection/services/inspection-details.service";
import { ItemsLightQuery } from "src/app/items/services/queries/items.light.query";
import { DefaultFormInfoInPreset } from "src/app/settings/models/preset";
import { PresetsService } from "src/app/settings/services/presets.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import { ItemsService } from "../../../items/services/items.service";
import { ITEM_TYPES } from "../../../items/static-forms/item-types";
import { InspectionCommonData } from "../../model/inspection-common-data";
import { InspectionState } from "../../model/inspection-state";
import { ItemLight } from "../../model/itemLight";
import { ProductsListConfig } from "../../permissions-setter/permissions-interface";
import { PermissionsService } from "../../services/permissions.service";
import { InspectionDetailsBase } from "../inspection-details-base";

export interface ProductDynamicListItem {
  hasForm: boolean;
}

@Component({
  selector: "im-inspection-details-items",
  templateUrl: "./inspection-details-items.component.html",
  styleUrls: ["./inspection-details-items.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InspectionDetailsItemsComponent
  extends InspectionDetailsBase
  implements OnInit
{
  public defaultFormId: string;
  public itemsAllowToDisableSections: boolean;
  public isLoading: boolean = true;
  public items: ItemLight[] = [];
  public itemId: string;
  public itemsListConfig: ProductsListConfig = {
    allowManageSections: false,
    allowOpenDetails: true,
    allowDelete: true,
    allowAddProduct: true,
    allowEdit: true,
  };

  private inspectionCommonDataSubscription: Subscription;
  private inspectionItemsSubscription: Subscription;
  private routeSubscription: Subscription;

  constructor(
    public activatedRoute: ActivatedRoute,
    private permissionsService: PermissionsService,
    private inspectionDetailsService: InspectionDetailsService,
    private itemsService: ItemsService,
    private userRolePermissionsService: UserRolePermissionsService,
    private presetService: PresetsService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    super(activatedRoute);
  }

  async ngOnInit() {
    const permissions = await this.permissionsService.getPermissions();
    this.itemsListConfig = permissions.setProductsListConfig();
    this.disableManageSections();
    this.subscribeToRouteParams();
    this.subscribeToInspectionCommonData();
    this.subscribeToInspectionItems();
    try {
      await this.getInspectionItems();
      await this.setPresetInfo();
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
    this.inspectionCommonDataSubscription?.unsubscribe();
    this.inspectionItemsSubscription?.unsubscribe();
  }

  private async setPresetInfo(): Promise<void> {
    const presetInfo: DefaultFormInfoInPreset =
      await this.presetService.getPresetDefaultFormId(this.inspectionId);
    this.defaultFormId = presetInfo.formId;
    this.itemsAllowToDisableSections = presetInfo.itemsAllowToDisableSections;
  }

  private disableManageSections(): void {
    this.itemsListConfig.allowManageSections = false;
  }

  private disableAddItemsWhenInspectionIsClosed(state: InspectionState): void {
    if (
      this.itemsListConfig.allowAddProduct &&
      state === InspectionState.Closed
    ) {
      this.itemsListConfig.allowAddProduct = false;
    }
  }

  private disableAddItemsWhenInspectionIsFinishedForInspector(
    state: InspectionState,
  ): void {
    if (
      this.itemsListConfig.allowAddProduct &&
      state === InspectionState.Finished &&
      this.userRolePermissionsService.isInspector()
    ) {
      this.itemsListConfig.allowAddProduct = false;
    }
  }

  private subscribeToRouteParams(): void {
    this.routeSubscription = this.activatedRoute.queryParams
      .pipe(take(1))
      .subscribe((params) => {
        this.itemId = params.itemId;
      });
  }

  private subscribeToInspectionCommonData(): void {
    if (!this.itemsListConfig?.isReadOnly) {
      this.inspectionCommonDataSubscription =
        this.inspectionDetailsService.inspectionCommonData.subscribe(
          (inspectionCommonData: InspectionCommonData) => {
            this.disableAddItemsWhenInspectionIsClosed(
              inspectionCommonData?.state,
            );
            this.disableAddItemsWhenInspectionIsFinishedForInspector(
              inspectionCommonData?.state,
            );
            if (
              !this.userRolePermissionsService.isInspector() &&
              !this.userRolePermissionsService.isServiceProviderInspector()
            ) {
              this.itemsListConfig.isReadOnly =
                inspectionCommonData?.state == InspectionState.Closed;
            } else {
              this.itemsListConfig.isReadOnly =
                inspectionCommonData?.state == InspectionState.Closed ||
                inspectionCommonData?.state == InspectionState.Finished;
            }
          },
        );
    }
  }

  private subscribeToInspectionItems(): void {
    this.inspectionItemsSubscription = this.itemsService.itemsLight$.subscribe(
      (items) => {
        this.ngZone.run(() => {
          this.setItemsOrderByType(items);
          this.items = [...items];
          this.changeDetectorRef.detectChanges();
        });
      },
    );
  }

  private async getInspectionItems() {
    await this.itemsService.invokeQuery(new ItemsLightQuery(this.inspectionId));
  }

  private setItemsOrderByType(items: ItemLight[]) {
    items.forEach((item) => (item["order"] = -1));
    ITEM_TYPES.forEach((type) => {
      const itemsByType = items.filter((item) =>
        item.name.startsWith(type.name),
      );
      itemsByType?.forEach((item, index) => (item["order"] = index + 1));
    });
  }
}
